import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Collapse,
  Tooltip,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import NoteIcon from '@mui/icons-material/Notes'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useNavigate } from 'react-router-dom'
import { getUrl, httpClient, putJson } from '../../providers/data'
import { PrognoseSetResponse } from '../../types/apiRequests'
import { statusColors, publicatieStatusColors } from './types'
import SheetViewer from './SheetViewer'
import EditPrognoseDialog from './EditPrognoseDialog'

interface PrognoseShowViewProps {
  record: PrognoseSetResponse | null
  loading: boolean
  subtitle?: string
  onRefetch: () => void
}

const statusLabels: Record<string, string> = {
  draft: 'Concept',
  calculating: 'Berekenen...',
  completed: 'Voltooid',
  error: 'Fout',
}

const PrognoseShowView: React.FC<PrognoseShowViewProps> = ({
  record,
  loading,
  subtitle,
  onRefetch,
}) => {
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [toelichting, setToelichting] = useState('')
  const [toelichtingSaving, setToelichtingSaving] = useState(false)
  const [toelichtingDirty, setToelichtingDirty] = useState(false)
  const [toelichtingOpen, setToelichtingOpen] = useState(false)

  // Poll for status when calculating
  useEffect(() => {
    if (record?.status !== 'calculating') return
    const interval = setInterval(() => {
      onRefetch()
    }, 3000)
    return () => clearInterval(interval)
  }, [record?.status, onRefetch])

  const handleExport = async () => {
    if (!record?.id) return
    try {
      const response = await httpClient(
        `${getUrl()}/prognose_sets/${record.id}/export`,
        { method: 'GET' }
      )
      const blob = new Blob([response.body], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${record.naam}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const handleEditSave = async (data: { naam: string; omschrijving: string; publicatie_status: string }) => {
    if (!record?.id) return
    try {
      await putJson(`${getUrl()}/prognose_sets/${record.id}`, data)
      setEditOpen(false)
      onRefetch()
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const handleDelete = async () => {
    if (!record?.id) return
    setDeleting(true)
    try {
      await httpClient(`${getUrl()}/prognose_sets/${record.id}`, {
        method: 'DELETE',
      })
      navigate(-1)
    } catch (err) {
      console.error('Delete failed:', err)
      setDeleting(false)
    }
  }

  // Initialize toelichting from record
  useEffect(() => {
    if (record?.omschrijving != null) {
      setToelichting(record.omschrijving)
      setToelichtingDirty(false)
      // Auto-expand if there's existing content
      if (record.omschrijving) {
        setToelichtingOpen(true)
      }
    }
  }, [record?.omschrijving])

  const handleSaveToelichting = async () => {
    if (!record?.id) return
    setToelichtingSaving(true)
    try {
      await putJson(`${getUrl()}/prognose_sets/${record.id}`, { omschrijving: toelichting })
      setToelichtingDirty(false)
      onRefetch()
    } catch (err) {
      console.error('Save toelichting failed:', err)
    } finally {
      setToelichtingSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!record) return null

  const status = record.status
  const publicatieStatus = (record.publicatie_status || 'concept')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const sheetNames: string[] = record.resultaat_summary?.sheet_names ?? []

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Compact header */}
      <Paper sx={{ px: 2, py: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Title & meta */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {record.naam}
              </Typography>
              <Chip
                label={publicatieStatus.charAt(0).toUpperCase() + publicatieStatus.slice(1)}
                color={publicatieStatusColors[publicatieStatus] || 'default'}
                size="small"
              />
              <Chip
                label={statusLabels[status] || status}
                color={statusColors[status] || 'default'}
                size="small"
                variant="outlined"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {subtitle ?? `Periode: ${record.bjaar} - ${record.ejaar}`}
              {' \u00B7 '}
              {new Date(record.created).toLocaleDateString('nl-NL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Toelichting">
              <IconButton
                size="small"
                onClick={() => setToelichtingOpen(!toelichtingOpen)}
                color={toelichting ? 'primary' : 'default'}
              >
                <NoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
            >
              Bewerken
            </Button>
            {status === 'completed' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => void handleExport()}
              >
                Excel export
              </Button>
            )}
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Verwijderen
            </Button>
          </Box>
        </Box>

        {/* Collapsible toelichting */}
        <Collapse in={toelichtingOpen}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Schrijf hier een toelichting..."
              value={toelichting}
              onChange={(e) => {
                setToelichting(e.target.value)
                setToelichtingDirty(true)
              }}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
            />
            {toelichtingDirty && (
              <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => void handleSaveToelichting()}
                  disabled={toelichtingSaving}
                >
                  {toelichtingSaving ? 'Opslaan...' : 'Opslaan'}
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {status === 'calculating' && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2">Berekening wordt uitgevoerd...</Typography>
          <Typography variant="caption" color="text.secondary">
            De pagina wordt automatisch ververst wanneer de berekening klaar is.
          </Typography>
        </Paper>
      )}

      {status === 'error' && (
        <Paper sx={{ p: 2 }}>
          <Typography color="error">
            Fout: {String(record.resultaat_summary?.error ?? 'Onbekende fout')}
          </Typography>
        </Paper>
      )}

      {status === 'completed' && sheetNames.length > 0 && (
        <SheetViewer prognoseSetId={record.id} sheetNames={sheetNames} />
      )}

      {/* Edit dialog */}
      {record && editOpen && (
        <EditPrognoseDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSave={(data) => void handleEditSave(data)}
          record={record}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Prognose verwijderen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Weet je zeker dat je de prognose &quot;{record.naam}&quot; wilt verwijderen?
            Dit kan niet ongedaan worden gemaakt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Annuleren
          </Button>
          <Button
            onClick={() => void handleDelete()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PrognoseShowView
